import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  fetchFilterOptions,
  fetchKnowledgePoints,
  createKnowledgePoint,
  updateKnowledgePoint,
  deleteKnowledgePoint,
  type KnowledgePoint,
  importKnowledgePoints,
} from '@/api/knowledgePoints'
import { cn } from '@/lib/utils'

interface TreeNode extends KnowledgePoint {
  children: TreeNode[]
}

function buildTree(items: KnowledgePoint[]): TreeNode[] {
  const map = new Map<string, TreeNode>()
  const roots: TreeNode[] = []

  items.forEach((kp) => {
    map.set(kp.kp_code, { ...kp, children: [] })
  })

  map.forEach((node) => {
    if (node.parent_code && map.has(node.parent_code)) {
      map.get(node.parent_code)!.children.push(node)
    } else {
      roots.push(node)
    }
  })

  roots.sort((a, b) => (a.kp_code > b.kp_code ? 1 : -1))
  map.forEach((node) => {
    node.children.sort((a, b) => (a.kp_code > b.kp_code ? 1 : -1))
  })

  return roots
}

interface NodeItemProps {
  node: TreeNode
  depth: number
  selectedCode: string | null
  onSelect: (node: TreeNode) => void
}

function NodeItem({ node, depth, selectedCode, onSelect }: NodeItemProps) {
  const [expanded, setExpanded] = useState(true)

  const hasChildren = node.children.length > 0

  return (
    <div>
      <div
        className={cn(
          'flex items-center rounded px-2 py-1 text-sm cursor-pointer hover:bg-accent',
          selectedCode === node.kp_code && 'bg-accent'
        )}
        style={{ paddingLeft: 8 + depth * 16 }}
        onClick={() => onSelect(node)}
      >
        {hasChildren && (
          <button
            type="button"
            className="mr-1 text-xs text-muted-foreground"
            onClick={(e) => {
              e.stopPropagation()
              setExpanded((v) => !v)
            }}
          >
            {expanded ? '−' : '+'}
          </button>
        )}
        {!hasChildren && <span className="mr-4 w-2" />}
        <span className="font-mono text-xs text-muted-foreground mr-2">
          {node.kp_code}
        </span>
        <span className="font-medium">{node.name}</span>
      </div>
      {expanded &&
        node.children.map((child) => (
          <NodeItem
            key={child.kp_code}
            node={child}
            depth={depth + 1}
            selectedCode={selectedCode}
            onSelect={onSelect}
          />
        ))}
    </div>
  )
}

const ALL_VALUE = 'ALL'

export default function KnowledgePointsPage() {
  const [stage, setStage] = useState<string | undefined>()
  const [subject, setSubject] = useState<string | undefined>()
  const [module, setModule] = useState<string | undefined>()

  const [filterOptions, setFilterOptions] = useState<{
    stage_options: string[]
    subject_options: string[]
    module_options: string[]
  }>({
    stage_options: [],
    subject_options: [],
    module_options: [],
  })

  const [rawItems, setRawItems] = useState<KnowledgePoint[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [selected, setSelected] = useState<TreeNode | null>(null)
  const [mode, setMode] = useState<'view' | 'edit' | 'create-child' | 'create-root'>('view')
  const [importing, setImporting] = useState(false)

  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const tree = useMemo(() => buildTree(rawItems), [rawItems])

  useEffect(() => {
    fetchFilterOptions({ stage, subject })
      .then(setFilterOptions)
      .catch(() => {
        setError('加载筛选选项失败')
      })
  }, [stage, subject])

  useEffect(() => {
    // 学段或学科变化时重置模块筛选
    setModule(undefined)
  }, [stage, subject])

  useEffect(() => {
    setLoading(true)
    setError(null)
    fetchKnowledgePoints({ stage, subject, module })
      .then((items) => {
        setRawItems(items)
        setSelected(null)
        setMode('view')
      })
      .catch(() => {
        setError('加载知识点失败')
      })
      .finally(() => setLoading(false))
  }, [stage, subject, module])

  const [form, setForm] = useState({
    kp_code: '',
    name: '',
    parent_code: '',
    level: 0,
    subject: '',
    stage: '',
    module: '',
    definition: '',
    can_do: '',
  })

  useEffect(() => {
    if (mode === 'edit' && selected) {
      setForm({
        kp_code: selected.kp_code,
        name: selected.name,
        parent_code: selected.parent_code || '',
        level: selected.level || 0,
        subject: selected.subject || '',
        stage: selected.stage || '',
        module: selected.module || '',
        definition: selected.definition || '',
        can_do: selected.can_do || '',
      })
    } else if (mode === 'create-child' && selected) {
      setForm({
        kp_code: '',
        name: '',
        parent_code: selected.kp_code,
        level: (selected.level || 0) + 1,
        subject: selected.subject || '',
        stage: selected.stage || '',
        module: selected.module || '',
        definition: '',
        can_do: '',
      })
    } else if (mode === 'create-root') {
      setForm({
        kp_code: '',
        name: '',
        parent_code: '',
        level: 0,
        subject: subject || '',
        stage: stage || '',
        module: module || '',
        definition: '',
        can_do: '',
      })
    }
  }, [mode, selected, stage, subject, module])

  async function handleSave() {
    if (!form.kp_code || !form.name) {
      setError('请填写编码和名称')
      return
    }
    setSaving(true)
    setError(null)
    try {
      if (mode === 'edit' && selected) {
        await updateKnowledgePoint(selected.kp_code, {
          name: form.name,
          parent_code: form.parent_code || null,
          level: form.level,
          subject: form.subject || null,
          stage: form.stage || null,
          module: form.module || null,
          definition: form.definition || null,
          can_do: form.can_do || null,
        })
      } else {
        await createKnowledgePoint({
          kp_code: form.kp_code,
          name: form.name,
          parent_code: form.parent_code || null,
          level: form.level,
          subject: form.subject || null,
          stage: form.stage || null,
          module: form.module || null,
          definition: form.definition || null,
          can_do: form.can_do || null,
          hit_rules: [],
          common_errors: [],
          tags: {},
        })
      }

      const items = await fetchKnowledgePoints({ stage, subject, module })
      setRawItems(items)
      setMode('view')
    } catch (e: any) {
      setError(e.message || '保存失败')
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete() {
    if (!selected) return
    if (!window.confirm(`确定删除知识点「${selected.name}」吗？`)) return
    setSaving(true)
    setError(null)
    try {
      await deleteKnowledgePoint(selected.kp_code)
      const items = await fetchKnowledgePoints({ stage, subject, module })
      setRawItems(items)
      setSelected(null)
      setMode('view')
    } catch (e: any) {
      setError(e.message || '删除失败')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <div className="mx-auto max-w-7xl px-6 py-8 space-y-6">
        <header className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">知识图谱管理</h1>
            <p className="text-sm text-slate-400">
              按学段、学科、模块筛选知识点，并在树状结构中维护知识图谱。
            </p>
          </div>
          <div className="flex gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept=".jsonl"
              className="hidden"
              onChange={async (e) => {
                const file = e.target.files?.[0]
                if (!file) return
                setImporting(true)
                setError(null)
                try {
                  const result = await importKnowledgePoints(file)
                  const items = await fetchKnowledgePoints({ stage, subject, module })
                  setRawItems(items)
                  window.alert(
                    `导入完成：新增 ${result.created}，更新 ${result.updated}，跳过 ${result.skipped}` +
                      (result.errors.length ? `\n部分错误示例：\n${result.errors.slice(0, 3).join('\n')}` : '')
                  )
                } catch (e: any) {
                  setError(e.message || '批量导入失败')
                } finally {
                  setImporting(false)
                  e.target.value = ''
                }
              }}
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={importing}
            >
              {importing ? '导入中…' : '批量导入'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setMode('create-root')
                setSelected(null)
              }}
            >
              新建根节点
            </Button>
          </div>
        </header>

        <div className="grid grid-cols-[minmax(0,1.5fr)_minmax(0,2fr)] gap-6">
          <Card className="bg-white border-slate-200">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center justify-between text-sm">
                <span>筛选条件</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <div className="mb-1 text-xs text-slate-500">学段</div>
                  <Select
                    value={stage ?? ALL_VALUE}
                    onValueChange={(v) => setStage(v === ALL_VALUE ? undefined : v)}
                  >
                    <SelectTrigger className="h-8 bg-white border-slate-200 text-xs">
                      <SelectValue placeholder="全部学段" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ALL_VALUE}>全部学段</SelectItem>
                      {filterOptions.stage_options.map((s) => (
                        <SelectItem key={s} value={s}>
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <div className="mb-1 text-xs text-slate-500">学科</div>
                  <Select
                    value={subject ?? ALL_VALUE}
                    onValueChange={(v) =>
                      setSubject(v === ALL_VALUE ? undefined : v)
                    }
                  >
                    <SelectTrigger className="h-8 bg-white border-slate-200 text-xs">
                      <SelectValue placeholder="全部学科" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ALL_VALUE}>全部学科</SelectItem>
                      {filterOptions.subject_options.map((s) => (
                        <SelectItem key={s} value={s}>
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <div className="mb-1 text-xs text-slate-500">模块</div>
                  <Select
                    value={module ?? ALL_VALUE}
                    onValueChange={(v) =>
                      setModule(v === ALL_VALUE ? undefined : v)
                    }
                  >
                    <SelectTrigger className="h-8 bg-white border-slate-200 text-xs">
                      <SelectValue placeholder="全部模块" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ALL_VALUE}>全部模块</SelectItem>
                      {filterOptions.module_options.map((s) => (
                        <SelectItem key={s} value={s}>
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="space-y-3 text-xs text-slate-500">
            <p>提示：左侧过滤条件改变时，会重新加载该维度下的知识点树。</p>
            <p>可以选择节点进行编辑，或为其新增子节点，支持删除节点。</p>
          </div>
        </div>

        <div className="grid grid-cols-[minmax(0,1.4fr)_minmax(0,2.1fr)] gap-6">
          <Card className="bg-white border-slate-200 overflow-hidden">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center justify-between text-sm">
                <span>知识点树</span>
                <span className="text-xs text-slate-400">
                  共 {rawItems.length} 个节点
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="max-h-[540px] overflow-auto border-t border-slate-100 pt-2">
              {loading ? (
                <div className="py-16 text-center text-sm text-slate-500">
                  正在加载知识点…
                </div>
              ) : tree.length === 0 ? (
                <div className="py-16 text-center text-sm text-slate-500">
                  当前筛选条件下暂无知识点。
                </div>
              ) : (
                <div className="space-y-1">
                  {tree.map((node) => (
                    <NodeItem
                      key={node.kp_code}
                      node={node}
                      depth={0}
                      selectedCode={selected?.kp_code || null}
                      onSelect={(n) => {
                        setSelected(n)
                        setMode('view')
                      }}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-white border-slate-200">
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-sm">节点详情与编辑</CardTitle>
                {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
              </div>
              <div className="flex gap-2">
                {selected && (
                  <>
                    <Button
                      size="sm"
                      variant={mode === 'edit' ? 'default' : 'outline'}
                      onClick={() => setMode('edit')}
                    >
                      编辑
                    </Button>
                    <Button
                      size="sm"
                      variant={mode === 'create-child' ? 'default' : 'outline'}
                      onClick={() => setMode('create-child')}
                    >
                      新建子节点
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-red-500/60 text-red-300 hover:bg-red-500/10 hover:text-red-100"
                      onClick={handleDelete}
                      disabled={saving}
                    >
                      删除
                    </Button>
                  </>
                )}
                {!selected && mode === 'create-root' && (
                  <span className="text-xs text-slate-500">正在创建根节点</span>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4 border-t border-slate-900/60 pt-4">
              {mode === 'view' && selected && (
                <div className="space-y-2 text-sm">
                  <div className="text-xs text-slate-500">
                    已选节点：<span className="font-mono">{selected.kp_code}</span>
                  </div>
                  <div className="text-lg font-semibold">{selected.name}</div>
                  {selected.definition && (
                    <p className="text-sm text-slate-800 whitespace-pre-line">
                      {selected.definition}
                    </p>
                  )}
                  {selected.can_do && (
                    <p className="text-xs text-slate-600 whitespace-pre-line">
                      能力要求：{selected.can_do}
                    </p>
                  )}
                </div>
              )}

              {(mode === 'edit' ||
                mode === 'create-child' ||
                mode === 'create-root') && (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <div className="mb-1 text-xs text-slate-500">编码 kp_code</div>
                      <Input
                        className="h-8 bg-white border-slate-200 text-xs"
                        placeholder="例如 MATH.JH.RJ.L3.NA.NUMBER_SYSTEM"
                        value={form.kp_code}
                        disabled={mode === 'edit'}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, kp_code: e.target.value }))
                        }
                      />
                    </div>
                    <div>
                      <div className="mb-1 text-xs text-slate-500">名称</div>
                      <Input
                        className="h-8 bg-white border-slate-200 text-xs"
                        value={form.name}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, name: e.target.value }))
                        }
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <div className="mb-1 text-xs text-slate-500">学段</div>
                      <Input
                        className="h-8 bg-white border-slate-200 text-xs"
                        value={form.stage}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, stage: e.target.value }))
                        }
                      />
                    </div>
                    <div>
                      <div className="mb-1 text-xs text-slate-500">学科</div>
                      <Input
                        className="h-8 bg-white border-slate-200 text-xs"
                        value={form.subject}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, subject: e.target.value }))
                        }
                      />
                    </div>
                    <div>
                      <div className="mb-1 text-xs text-slate-500">模块</div>
                      <Input
                        className="h-8 bg-white border-slate-200 text-xs"
                        value={form.module}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, module: e.target.value }))
                        }
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-[1fr_auto] gap-3 items-center">
                    <div>
                      <div className="mb-1 text-xs text-slate-500">父节点编码</div>
                      <Input
                        className="h-8 bg-white border-slate-200 text-xs"
                        value={form.parent_code}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, parent_code: e.target.value }))
                        }
                      />
                    </div>
                    <div>
                      <div className="mb-1 text-xs text-slate-500">层级 level</div>
                      <Input
                        type="number"
                        className="h-8 w-20 bg-white border-slate-200 text-xs"
                        value={form.level}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            level: Number(e.target.value) || 0,
                          }))
                        }
                      />
                    </div>
                  </div>

                  <div>
                    <div className="mb-1 text-xs text-slate-500">定义 / 概念描述</div>
                    <Textarea
                      className="min-h-[80px] bg-white border-slate-200 text-xs"
                      value={form.definition}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, definition: e.target.value }))
                      }
                    />
                  </div>

                  <div>
                    <div className="mb-1 text-xs text-slate-500">能力描述（能做什么）</div>
                    <Textarea
                      className="min-h-[60px] bg-white border-slate-200 text-xs"
                      value={form.can_do}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, can_do: e.target.value }))
                      }
                    />
                  </div>

                  <div className="flex justify-end gap-2 pt-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-xs text-slate-500"
                      onClick={() => setMode('view')}
                      disabled={saving}
                    >
                      取消
                    </Button>
                    <Button
                      size="sm"
                      className="text-xs"
                      onClick={handleSave}
                      disabled={saving}
                    >
                      {saving ? '保存中…' : '保存节点'}
                    </Button>
                  </div>
                </div>
              )}

              {mode === 'view' && !selected && (
                <div className="text-sm text-slate-500">
                  在左侧选择一个节点查看详情，或点击右上角「新建根节点」开始构建新的知识树。
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
